import { Builder } from '@builder.io/react'
import CdnImage from 'components/util/CdnImage'
import { triggerTagging } from 'components/util/common-utils'
import { HeadingClass, HeadingType } from 'enums'
import React, { useEffect, useState } from 'react'
import Bullet from '../../../images/BulletPoint.svg'
import CustomHeading, { headingTypePropObj, headingClassPropObj } from '../common/heading'

enum ImageSide {
  Left = 'left',
  Right = 'right',
  None = 'none'
}

enum ButtonPosition {
  Left = 'left',
  Right = 'right',
  Center = 'center'
}

interface Props {
  headingText: string
  headingTextColor: string
  headingType: HeadingType
  headingClass: HeadingClass
  headerBackground: any
  headerBackgroundMobile: any
  backgroundColor: string
  imageSide: ImageSide
  builderImage: any
  builderMobileImage: any
  imageAltText: string
  ctaButtonShow: boolean
  ctaButtonPosition: ButtonPosition
  bulletPoint: any
  bulletAltText: string
  points: [{ text: string }]
  switchTitleAndImageMobile: boolean
  coupon: string
}

const BulletSection = ({
  headingText,
  headingTextColor,
  headingType,
  headingClass,
  headerBackground,
  headerBackgroundMobile,
  backgroundColor,
  imageSide,
  builderImage,
  builderMobileImage,
  imageAltText = '',
  ctaButtonShow,
  ctaButtonPosition,
  bulletPoint,
  bulletAltText = '',
  points,
  switchTitleAndImageMobile,
  coupon = ''
}: Props) => {
  const [turboRouteComponent, setTurboRouteComponent] = useState<HTMLElement | null>(null)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const turboRoute = document.querySelector('#turboRoute')
      if (turboRoute) {
        setTurboRouteComponent(turboRoute as HTMLElement)
      }
    }
  }, [])

  const getBulletContent = () => {
    return (
      <div className="word-break-fix max-w-prose text-base">
        <div className="max-h-full w-full mt-4 mb-4 flex max-w-full flex-col">
          {points?.map((point, i) => (
            <div className="flex items-center" key={i}>
              <img src={bulletPoint || Bullet} className="mr-2 h-8 w-8" alt={bulletAltText} />
              <p className="text-left font-medium font-sans text-base">{point.text}</p>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const getContent = () => {
    if (imageSide === ImageSide.Left) {
      return (
        <>
          <div className="lg:absolute lg:inset-0">
            <div className="top-auto lg:absolute lg:inset-y-0 lg:left-0 lg:top-1/2 lg:h-full lg:w-1/2 lg:-translate-y-1/2">
              <CdnImage
                className="hidden h-full max-h-full object-contain lg:absolute lg:left-0 lg:block"
                url={builderImage}
                alt={imageAltText}
                steps={[705]}
                style={{
                  width: '705px',
                  height: '1275px',
                  maxHeight: '100%',
                  maxWidth: '100%'
                }}
              />

              <CdnImage
                className="block object-contain lg:absolute lg:hidden"
                url={builderMobileImage}
                alt={imageAltText}
                steps={[480]}
                style={{
                  width: '100%',
                  height: '100%',
                  maxHeight: '18rem'
                }}
              />
            </div>
          </div>

          <div
            className={`line-height-fix w-full mx-auto lg:w-full h-full tracking-tight text-base font-medium text-center font-serif text-2xl mt-6 ${
              switchTitleAndImageMobile ? 'block lg:hidden' : ''
            }`}
          >
            {headingText}
          </div>
          <div className="relative h-full w-full 2xl:w-5/6 px-4 pt-4 pb-6 sm:px-6 sm:pt-4 lg:mx-auto lg:grid lg:max-w-7xl lg:grid-cols-2 lg:px-8">
            <div className="col-span-1 lg:col-start-2 lg:pl-12 2xl:pl-0">
              <div className="word-break-fix mx-auto max-w-prose text-base lg:ml-auto lg:mr-0">
                <div className="max-h-ful mt-4 mb-4 flex max-w-full flex-col gap-y-3">
                  {points?.map((point, i) => (
                    <div className="flex items-center" key={i}>
                      <img src={bulletPoint || Bullet} className="mr-2 h-8 w-8" alt={bulletAltText} />
                      <p className="text-left font-medium font-sans text-base">{point.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )
    }

    return (
      <>
        <div className="mx-auto flex h-full w-full flex-col lg:flex-row 2xl:container items-center -mt-16">
          <div className="mx-auto h-full max-h-[1000px] min-h-[18rem] lg:min-h-[20rem] w-full justify-center pt-20 text-center lg:w-1/2 lg:text-left">
            <div className="col-span-2 lg:col-start-1 pl-16">
              <div className="word-break-fix mx-auto max-w-prose text-base">
                <div className="mx-auto mt-4 mb-4 flex max-h-full max-w-full flex-col gap-y-3 md:ml-10 md:gap-y-1 lg:ml-0">
                  {points?.map((point, i) => (
                    <div className="flex items-start" key={i}>
                      <img src={bulletPoint || Bullet} className="mr-2 h-8 w-8" alt={bulletAltText} />
                      <p className="text-left font-medium font-sans text-base">{point.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {ctaButtonPosition === 'left' && ctaButtonShow && (
              <button
                type="button"
                className={` mx-auto mt-6 flex w-4/5 justify-center rounded-3xl border border-transparent bg-primary px-4 py-2 text-base font-medium hover:underline md:text-lg md:w-1/2 lg:ml-40`}
                onClick={() => {
                  if (turboRouteComponent) {
                    triggerTagging('bullet_section_cta_turbo_route', 'turbo_route')
                    turboRouteComponent.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  } else {
                    window.location.href = `/signup/dog-details/${coupon ? `?coupon=${coupon}` : ''}`
                  }
                }}
                style={{ color: '#fff' }}
              >
                {turboRouteComponent ? 'Get Started' : 'Switch to Fresh'}
              </button>
            )}
          </div>
          <div className="lg:mt-8 grid lg:block w-full lg:w-1/2 lg:pt-8">
            <div className="max-h-448 relative mx-auto lg:mb-12 h-auto xl:w-[70%] rounded-lg lg:h-[21rem]">
              <CdnImage
                className="index-2 height-specifier relative h-full object-cover flex items-center"
                url={builderImage}
                alt={imageAltText}
                steps={[315, 390, 515, 640]}
              />
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      {imageSide === ImageSide.Left && (
        <div className="" style={{ backgroundColor: `${backgroundColor}` }}>
          <CustomHeading
            classList={`line-height-fix w-4/5 mx-auto lg:w-full h-full tracking-tight text-gray-900 font-medium text-center ${
              switchTitleAndImageMobile ? 'hidden lg:block' : ''
            }`}
            headingClass={headingClass}
            headingType={headingType}
            color={headingTextColor}
          >
            {headingText}
          </CustomHeading>
          <div
            className="relative mx-auto flex w-4/5 lg:w-10/12 flex-col lg:flex-row items-center justify-center bg-white bg-auto bg-no-repeat lg:bg-100% mt-4 xl:my-8"
            style={{ backgroundColor: `${backgroundColor}` }}
          >
            {getContent()}
          </div>
          {ctaButtonPosition === 'center' && ctaButtonShow && (
            <button
              type="button"
              className={` mx-auto mt-2 flex w-4/5 justify-center rounded-3xl border border-transparent bg-primary px-4 py-2 text-base font-medium hover:underline md:text-lg md:w-1/2 lg:w-1/4`}
              onClick={() => {
                if (turboRouteComponent) {
                  triggerTagging('bullet_section_cta_turbo_route', 'turbo_route')
                  turboRouteComponent.scrollIntoView({ behavior: 'smooth', block: 'start' })
                } else {
                  window.location.href = `/signup/dog-details/${coupon ? `?coupon=${coupon}` : ''}`
                }
              }}
              style={{ color: '#fff' }}
            >
              {turboRouteComponent ? 'Get Started' : 'Switch to Fresh'}
            </button>
          )}
        </div>
      )}
      {imageSide === ImageSide.Right && (
        <>
          <div
            className="relative mx-auto hidden h-28 w-10/12 bg-contain bg-bottom bg-no-repeat lg:block 2xl:h-56"
            style={{
              backgroundImage: headerBackground ? `url(${headerBackground})` : 'none'
            }}
          />
          <div
            className="relative flex mx-auto h-12 xs:h-16 sm:h-20 md:h-30 w-[90%] bg-cover bg-no-repeat lg:hidden"
            style={{
              backgroundImage: headerBackgroundMobile ? `url(${headerBackgroundMobile})` : 'none'
            }}
          />

          <div
            className="relative mx-auto w-[90%] rounded-b-3xl pb-4 text-center md:block lg:w-10/12 lg:px-8"
            style={{ backgroundColor }}
          >
            <div className="-mt-4">
              <CustomHeading headingClass={headingClass} headingType={headingType} color={headingTextColor}>
                {headingText}
              </CustomHeading>
            </div>

            {getContent()}
          </div>
        </>
      )}
      {imageSide === ImageSide.None && (
        <>
          {headerBackground && (
            <div
              className="relative mx-auto hidden h-28 w-10/12 bg-contain bg-bottom bg-no-repeat lg:block 2xl:h-56"
              style={{
                backgroundImage: headerBackground ? `url(${headerBackground})` : 'none'
              }}
            />
          )}
          {headerBackgroundMobile && (
            <div
              className="relative flex mx-auto h-12 xs:h-16 sm:h-20 md:h-30 w-[90%] bg-cover bg-no-repeat lg:hidden"
              style={{
                backgroundImage: headerBackgroundMobile ? `url(${headerBackgroundMobile})` : 'none'
              }}
            />
          )}
          <div className="relative mx-auto w-full rounded-b-3xl pb-4 text-left md:block" style={{ backgroundColor }}>
            <div className="-mt-4">
              <CustomHeading headingClass={headingClass} headingType={headingType} color={headingTextColor}>
                {headingText}
              </CustomHeading>
            </div>

            {getBulletContent()}
          </div>
        </>
      )}
    </>
  )
}

export default BulletSection

Builder.registerComponent(BulletSection, {
  name: 'BulletSection',
  inputs: [
    {
      ...headingTypePropObj
    },
    {
      ...headingClassPropObj
    },
    {
      name: 'headingText',
      type: 'text',
      defaultValue: 'Javascript for Beginners'
    },
    {
      name: 'headingTextColor',
      type: 'color',
      defaultValue: 'black'
    },
    {
      name: 'headerBackground',
      type: 'file',
      defaultValue: null
    },
    {
      name: 'headerBackgroundMobile',
      type: 'file',
      defaultValue: null
    },
    {
      name: 'backgroundColor',
      type: 'color',
      defaultValue: 'black'
    },
    {
      name: 'imageSide',
      type: 'string',
      enum: ['left', 'right', 'none'],
      defaultValue: 'left'
    },
    {
      name: 'builderImage',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },
    {
      name: 'builderMobileImage',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },
    {
      name: 'imageAltText',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'ctaButtonShow',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'ctaButtonPosition',
      type: 'string',
      enum: ['left', 'right', 'center'],
      defaultValue: 'center'
    },
    {
      name: 'bulletPoint',
      type: 'file',
      defaultValue: Bullet
    },
    {
      name: 'bulletAltText',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'points',
      type: 'list',
      subFields: [{ name: 'text', type: 'string' }],
      defaultValue: []
    },
    {
      name: 'switchTitleAndImageMobile',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'coupon',
      type: 'text',
      defaultValue: ''
    }
  ]
})
